import { useSyncCredits } from "@package/api-client/hooks/billingController";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

// Post-checkout / post-portal landing. Stripe redirects here (the server tags the
// return URL with ?orgId). We force a sync to reconcile the just-completed charge,
// then bounce to the org's billing page.
export const Route = createFileRoute("/_auth/app/billing/return")({
	validateSearch: (search: Record<string, unknown>): { orgId?: string } => ({
		orgId: typeof search.orgId === "string" ? search.orgId : undefined,
	}),
	component: BillingReturnPage,
});

function BillingReturnPage() {
	const { t } = useTranslation();
	const { orgId } = Route.useSearch();
	const sync = useSyncCredits();
	const fired = useRef(false);

	useEffect(() => {
		if (fired.current) return;
		fired.current = true;

		const target = orgId ? `/app/org/${orgId}/billing` : "/app/select-organization";

		// Force the Stripe sync, then hard-redirect into the org area. The bounce is
		// driven by the mutateAsync() promise's `.finally` (NOT the mutation's render
		// state or a mutate() callback) so it fires even if the component unmounts —
		// robust against React StrictMode's dev double-mount. `location.replace`
		// keeps the spinner out of history; we redirect on error too (e.g. Stripe
		// not configured → 503), since the balance page still loads.
		if (!orgId) {
			window.location.replace(target);
			return;
		}
		sync
			.mutateAsync({ orgId })
			.catch(() => {})
			.finally(() => window.location.replace(target));
	}, [orgId, sync]);

	return (
		<div className="flex flex-col items-center justify-center min-h-[50vh] gap-3">
			<span className="loading loading-spinner loading-md" />
			<p className="text-sm text-base-content/60">{t("billing.syncing")}</p>
		</div>
	);
}
